import { useSiteInfo } from '@contexts/SiteInfoContext';
import { Swiper, SwiperSlide } from 'swiper/react';
import SiteMapping from '@configs/siteMapping';
import { useContext, useEffect, useState, useRef } from 'react';
import { HeaderContext } from '@contexts/HeaderContext';
import { swiperConfigNova88, swiperConfigBong88, swiperConfigViva88 } from './swiperConfig';
import { useWindowDimensions } from '../../hooks/windowDimension';
import PaginationBullet from './PaginationBullet';
import PrevSlideButton from './PrevSlideButton';
import NextSlideButton from './NextSlideButton';
import breakPoint from '../../utils/constants';
import ErrorBoundary from '../common/ErrorBoundary';

const composeImageUrlWithLanguage = (banners, { language, loginWebImageServerHost }) => {
    return banners?.map(({ imageUrl, imageUrlV2, ...rest }) => {
        // New CMS flow: imageUrlV2 is already an absolute URL (api-backend prepends the S3 domain),
        // with the locale segment left as a {0} placeholder. Only the locale needs substituting,
        // so loginWebImageServerHost is not applied here.
        if (imageUrlV2) {
            return { imageUrl: imageUrlV2.replace('{0}', language), ...rest };
        }
        // Legacy flow (kept for backward compatibility): imageUrl is a relative path, so swap the
        // locale segment and prepend the ImageServer host. Paths without an /en/ segment are left as is.
        const splitedUrl = imageUrl.split('/en/');
        if (splitedUrl.length === 1) return { imageUrl, ...rest };
        return { imageUrl: `${loginWebImageServerHost}/${splitedUrl[0]}/${language}/${splitedUrl[1]}`, ...rest };
    });
};

const openBannerLink = (link) => {
    if (!link) return;
    window.open(link, '_blank');
    // window.open('https://www.taisaba.com/', '_blank');
};

const PromotionSwiper = ({ swiperConfig }) => {
    const {
        siteName, loginWebImageServerHost, desktopBanners, mobileBanners, youTubeBrandVideoId
    } = useSiteInfo();
    

    const { width } = useWindowDimensions();
    const [isMoreThanOneDesktopImage, setIsMoreThanOneDesktopImage] = useState(true);
    const [isMoreThanOneMobileImage, setIsMoreThanOneMobileImage] = useState(true);
    const [isLoop, setIsLoop] = useState(false);
    const [isDomLoaded, setIsDomLoaded] = useState(false);
    const [state] = useContext(HeaderContext);
    const { language } = state;
    const composeImageData = { language, loginWebImageServerHost };
    const { lazy: isLazyLoading } = swiperConfig;
    
    const swiperRef = useRef(null);

    useEffect(() => {
        setIsDomLoaded(true);
    }, []);

    useEffect(() => {
        if (desktopBanners?.length === 1) setIsMoreThanOneDesktopImage(false);
        if (mobileBanners?.length === 1) setIsMoreThanOneMobileImage(false);
    }, [desktopBanners, mobileBanners]);

    useEffect(() => {
        // if (siteName === SiteMapping.viva88) return;
        if (width > breakPoint.mediumWidth) {
            setIsLoop(isMoreThanOneDesktopImage);
        } else {
            setIsLoop(isMoreThanOneMobileImage);
        }
        
    }, [width]);


    useEffect(() => {
        if (siteName === SiteMapping.viva88 && isDomLoaded) {
            const handleSlideChange = () => {
                // Pause all YouTube videos when slide changes
                const youtubeIframes = document.querySelectorAll('.swiper__item--youtube iframe');
                youtubeIframes.forEach(iframe => {
                    // Send postMessage to pause the video
                    if (iframe.contentWindow) {
                        iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
                    }
                });
                
                // Stop swiper autoplay when slide changes
                if (swiperRef.current && swiperRef.current.autoplay) {
                    swiperRef.current.autoplay.stop();
                }
            };

            const swiperElement = document.querySelector('.swiper');
            if (swiperElement && swiperElement.swiper) {
                swiperElement.swiper.on('slideChange', handleSlideChange);
                
                return () => {
                    if (swiperElement.swiper) {
                        swiperElement.swiper.off('slideChange', handleSlideChange);
                    }
                };
            }
        }
    }, [siteName, isDomLoaded]);


    if (!isDomLoaded) return [];
    // Prevent from getting no images.
    if ((width > breakPoint.mediumWidth && !desktopBanners?.length)
        || (width < breakPoint.mediumWidth && !mobileBanners?.length)) {
        return [];
    }
    
    return (
        <>
            <Swiper
                {...swiperConfig}
                loop={isLoop}
                onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                }}
            >
                {siteName === SiteMapping.viva88 && width > breakPoint.mediumWidth && !!youTubeBrandVideoId && (
                    <SwiperSlide key="viva88-video" data-swiper-autoplay="48000">
                        <div className="swiper__item swiper__item--youtube">
                            <iframe 
                                id="youtube-player-desktop"
                                width="560" 
                                height="315" 
                                src={`https://www.youtube.com/embed/${youTubeBrandVideoId}?autoplay=1&mute=1&loop=1&controls=1&playlist=${youTubeBrandVideoId}&rel=0&modestbranding=1&enablejsapi=1`}
                                title="YouTube video player" 
                                frameBorder="0" 
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                                referrerPolicy="strict-origin-when-cross-origin" 
                                allowFullScreen
                            />
                            {/* Navigation arrows for YouTube slide */}
                            <div className="promotions__swiper-button-prev" tabIndex="0" role="button" aria-label="Previous slide">
                                <i className="icon icon--arrow-left" />
                            </div>
                            <div className="promotions__swiper-button-next" tabIndex="0" role="button" aria-label="Next slide">
                                <i className="icon icon--arrow-right" />
                            </div>
                        </div>
                    </SwiperSlide>
                )}
                
                {width > breakPoint.mediumWidth
                    ? composeImageUrlWithLanguage(desktopBanners, composeImageData).map(({ imageUrl, link }) => (
                        <SwiperSlide key={imageUrl} onClick={() => openBannerLink(link)}>
                            <div className="swiper__item">
                                <picture className="swiper-lazy">
                                    <source
                                        media="(min-width: 769px)"
                                        srcSet={imageUrl}
                                        loading={isLazyLoading ? 'lazy' : ''}
                                    />
                                    <source
                                        media="(max-width: 768px)"
                                        srcSet={imageUrl}
                                        loading={isLazyLoading ? 'lazy' : ''}
                                    />
                                    <img
                                        alt=""
                                        src={imageUrl}
                                        loading={isLazyLoading ? 'lazy' : ''}
                                    />
                                </picture>
                                {isLazyLoading && <div className="swiper-lazy-preloader" />}
                            </div>
                        </SwiperSlide>
                    ))
                   
                    : (
                        <>
                            {siteName === SiteMapping.viva88 && !!youTubeBrandVideoId && (
                                <SwiperSlide key="viva88-video-mobile" data-swiper-autoplay="48000">
                                    <div className="swiper__item swiper__item--youtube" data-open="false">
                                        <iframe 
                                            id="youtube-player-mobile"
                                            width="560" 
                                            height="315" 
                                            src={`https://www.youtube.com/embed/${youTubeBrandVideoId}?autoplay=1&mute=1&loop=1&controls=1&playlist=${youTubeBrandVideoId}&rel=0&modestbranding=1&enablejsapi=1`}
                                            title="YouTube video player" 
                                            frameBorder="0" 
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                                            referrerPolicy="strict-origin-when-cross-origin" 
                                            allowFullScreen
                                        />
                                        {/* Navigation arrows for mobile YouTube slide */}
                                        <div className="promotions__swiper-button-prev" tabIndex="0" role="button" aria-label="Previous slide">
                                            <i className="icon icon--arrow-left" />
                                        </div>
                                        <div className="promotions__swiper-button-next" tabIndex="0" role="button" aria-label="Next slide">
                                            <i className="icon icon--arrow-right" />
                                        </div>
                                    </div>
                                </SwiperSlide>
                            )}
                            
                            {composeImageUrlWithLanguage(mobileBanners, composeImageData)?.map(({ imageUrl, link }) => (
                                <SwiperSlide key={imageUrl} onClick={() => openBannerLink(link)}>
                                    <div className="swiper__item">
                                        <picture className="swiper-lazy">
                                            <source
                                                media="(min-width: 769px)"
                                                srcSet={imageUrl}
                                                loading={isLazyLoading ? 'lazy' : ''}
                                            />
                                            <source
                                                media="(max-width: 768px)"
                                                srcSet={imageUrl}
                                                loading={isLazyLoading ? 'lazy' : ''}
                                            />
                                            <img
                                                alt=""
                                                src={imageUrl}
                                                loading={isLazyLoading ? 'lazy' : ''}
                                            />
                                        </picture>
                                        {isLazyLoading && <div className="swiper-lazy-preloader" />}
                                    </div>
                                </SwiperSlide>
                            ))}
                        </>
                    )}

                {/* pagination */}
                <PaginationBullet siteName={siteName} />
            </Swiper>
            {
                {
                    [SiteMapping.Nova88]: (
                        <>
                            { isMoreThanOneDesktopImage && (
                                <>
                                    <PrevSlideButton />
                                    <NextSlideButton />
                                </>
                            )}
                        </>
                    ),
                    [SiteMapping.IBCBet]: (
                        <>
                            { isMoreThanOneDesktopImage && (
                                <>
                                    <PrevSlideButton />
                                    <NextSlideButton />
                                </>
                            )}
                        </>
                    ),
                    [SiteMapping.PICA88]: (
                        <>
                            { isMoreThanOneDesktopImage && (
                                <>
                                    <PrevSlideButton />
                                    <NextSlideButton />
                                </>
                            )}
                        </>
                    ),
                    [SiteMapping.id8899]: (
                        <>
                            { isMoreThanOneDesktopImage && (
                                <>
                                    <PrevSlideButton />
                                    <NextSlideButton />
                                </>
                            )}
                        </>
                    ),
                    [SiteMapping.bettha88]: (
                        <>
                            { isMoreThanOneDesktopImage && (
                                <>
                                    <PrevSlideButton />
                                    <NextSlideButton />
                                </>
                            )}
                        </>
                    ),
                    [SiteMapping.taraPlays]: (
                        <>
                            {isMoreThanOneDesktopImage && (
                                <>
                                    <PrevSlideButton />
                                    <NextSlideButton />
                                </>
                            )}
                        </>
                    ),
                    [SiteMapping.fifabet]: (
                        <>
                            { isMoreThanOneDesktopImage && (
                                <>
                                    <PrevSlideButton />
                                    <NextSlideButton />
                                </>
                            )}
                        </>
                    ),
                    [SiteMapping.ocean99]: (
                        <>
                            { isMoreThanOneDesktopImage && (
                                <>
                                    <PrevSlideButton />
                                    <NextSlideButton />
                                </>
                            )}
                        </>
                    ),
                }[siteName]

            }

        </>
    );
};
const Promotion = () => {
    const { siteName } = useSiteInfo();

    return (
        <ErrorBoundary>
            {{
                [SiteMapping.Nova88]: (
                    <div className="promotions">
                        <PromotionSwiper swiperConfig={swiperConfigNova88} />
                    </div>),
                [SiteMapping.IBCBet]: (
                    <div className="promotions">
                        <PromotionSwiper swiperConfig={swiperConfigNova88} />
                    </div>),
                [SiteMapping.id8899]: (
                    <div className="promotions">
                        <PromotionSwiper swiperConfig={swiperConfigNova88} />
                    </div>),
                [SiteMapping.viva88]: <PromotionSwiper swiperConfig={swiperConfigViva88} />,
                [SiteMapping.bong88]: <PromotionSwiper swiperConfig={swiperConfigBong88} />,
                [SiteMapping.bettha88]: (
                    <div className="promotions">
                        <PromotionSwiper swiperConfig={swiperConfigNova88} />
                    </div>),
                [SiteMapping.taraPlays]: (
                    <div className="promotions">
                        <PromotionSwiper swiperConfig={swiperConfigNova88} />
                    </div>),
                [SiteMapping.fifabet]: (
                    <div className="promotions">
                        <PromotionSwiper swiperConfig={swiperConfigNova88} />
                    </div>),
                [SiteMapping.ocean99]: (
                    <div className="promotions">
                        <PromotionSwiper swiperConfig={swiperConfigNova88} />
                    </div>),
            }[siteName]}
        </ErrorBoundary>
    );
};

export default Promotion;
